import {
  Button,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import {
  useFormData,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";
import {
  Suffix,
  translateField,
} from "@totalenergiescode/mobility-business-rev-form-data/components";

import {
  StyledBlock,
  StyledCheckbox,
  StyledContainer,
  StyledDayLabel,
  StyledDaysContainer,
  StyledErrorText,
  StyledRefreshIcon,
  StyledXIcon,
} from "./styles";

const CHECKED_DAY = "Y";
const UNCHECKED_DAY = "N";

export interface DaysTranslation {
  day: string;
  trad: string;
}

export type Week<T> = [T, T, T, T, T, T, T];

export interface MultiselectDaysProps {
  fieldId: string;
  resetButtonTranslation: string;
  daysTranslation: Week<DaysTranslation>;
}

export function MultiselectDays({
  fieldId,
  resetButtonTranslation,
  daysTranslation,
}: MultiselectDaysProps) {
  const findFieldById = useFormData((fd) => fd.findFieldById);
  const field = findFieldById(fieldId);

  const authorizedWeekDays = field.value.asString();
  const value = authorizedWeekDays.value() || authorizedWeekDays.defaultValue();

  const { translatorFor, dataTestIdPrefix } = useFormDataContext();
  const translator = translatorFor(field);
  const label = translateField(translator, field, Suffix.LABEL);

  const disabled = !field.writable;
  const errorMessage = field.violations().join(", ");

  function handleReset() {
    field.setValue("");
  }

  function handleDaySelection(checked: boolean, index: number) {
    const daySelection = checked ? CHECKED_DAY : UNCHECKED_DAY;

    const valueToReplace =
      authorizedWeekDays.value() || authorizedWeekDays.defaultValue();

    field.setValue(replaceCharAt(valueToReplace, index, daySelection));
  }

  function replaceCharAt(input: string, index: number, newChar: string) {
    const output = input.split("");
    output[index] = newChar;

    return output.join("");
  }

  return (
    <StyledContainer data-testid={"test-id"}>
      <Text variant='title2' fontWeight='bold'>
        {label.toString()}
      </Text>
      <StyledDaysContainer>
        {daysTranslation.map((day, index) => (
          <StyledDayLabel key={day.day}>
            {translator.translate(day.trad)}
            <StyledBlock>
              <StyledCheckbox
                disabled={disabled}
                checked={value[index] === CHECKED_DAY}
                onChange={(_, checked) => {
                  handleDaySelection(checked, index);
                }}
                checkedIcon={<StyledXIcon width={27} height={27} />}
                dataTestId={`${dataTestIdPrefix}-checkbox-${day.day}`}
              />
            </StyledBlock>
          </StyledDayLabel>
        ))}
      </StyledDaysContainer>

      {errorMessage && (
        <StyledErrorText
          variant='text1'
          dataTestId={`${dataTestIdPrefix}-error-text`}
        >
          {errorMessage}
        </StyledErrorText>
      )}

      <Button
        onClick={handleReset}
        buttonText={translator.translate(resetButtonTranslation)} // the correct translation
        size='s'
        isLowercase
        rightIcon={<StyledRefreshIcon width={22} height={22} />}
        variant='tertiary'
        disabled={disabled}
        dataTestId={`${dataTestIdPrefix}-reset-button`}
      />
    </StyledContainer>
  );
}
